import React from "react";
import { Input } from "../../../common/u1/Input";
import { SelectField } from "../../../common/u1/SelectField";
import { ReadOnlyField } from "../../../common/u1/ReadOnlyField";
import { FieldLabel } from "../../../common/u1/FieldLabel";
import { codeToFlagEmoji } from "../../../common/u1/countryFlag";
import { DAYS, timeOptions, DEFAULT_WORKING_HOURS } from "./workingHours";

/* Step 4 — the registered address and working hours. */
export const AddressStep = ({
  line1 = "",
  line2 = "",
  area = "",
  city = "",
  postalCode = "",
  onFieldChange = () => {},
  country = "",
  countryCode = "",
  /* Owned by this step since ICC-03 made the licence skippable: the API
     requires a non-blank address_state, so it cannot live only on a step the
     user may never fill in. Pre-filled when the licence step was used. */
  state = "",
  onStateChange = () => {},
  stateOptions = [],
  workingHours = DEFAULT_WORKING_HOURS,
  onWorkingHoursChange = () => {},
  errors = {},
}) => {
  const times = timeOptions();
  const setHours = (patch) => onWorkingHoursChange({ ...workingHours, ...patch });

  return (
    <div className="grid gap-3.5">
      <Input
        id="claim-addr-1"
        label="Address Line 1"
        labelVariant="caption"
        required
        value={line1}
        onChange={(e) => onFieldChange("line1", e.target.value)}
        placeholder="Office 204, Building 5"
        error={errors.line1}
      />
      <Input
        id="claim-addr-2"
        label="Address Line 2"
        labelVariant="caption"
        value={line2}
        onChange={(e) => onFieldChange("line2", e.target.value)}
        placeholder="Al Hamra Street"
        error={errors.line2}
      />

      <div className="grid gap-3.5 sm:grid-cols-2 md:grid-cols-3">
        <Input
          id="claim-area"
          label="Area"
          labelVariant="caption"
          value={area}
          onChange={(e) => onFieldChange("area", e.target.value)}
          placeholder="Deira"
          error={errors.area}
        />
        <Input
          id="claim-city"
          label="City"
          labelVariant="caption"
          required
          value={city}
          onChange={(e) => onFieldChange("city", e.target.value)}
          placeholder="Dubai"
          error={errors.city}
        />
        <Input
          id="claim-postal"
          label="Postal Code"
          labelVariant="caption"
          value={postalCode}
          onChange={(e) => onFieldChange("postalCode", e.target.value)}
          placeholder="00000"
          error={errors.postalCode}
        />
      </div>

      <div className="grid gap-3.5 sm:grid-cols-2">
        <ReadOnlyField
          label="Country"
          value={country}
          prefix={countryCode ? codeToFlagEmoji(countryCode) : null}
        />
        <SelectField
          id="claim-addr-state"
          label="State / Emirate"
          required
          value={state}
          onChange={(e) => onStateChange(e.target.value)}
          options={stateOptions}
          placeholder={stateOptions.length ? "Select a state" : "No states available"}
          error={errors.state}
        />
      </div>

      {/* A real <legend> straddles the fieldset's border by default. This is a
          plain labelled group instead, so the caption sits inside the box —
          role + aria-labelledby give assistive tech the same grouping. */}
      <div
        role="group"
        aria-labelledby="claim-working-hours"
        className="mt-1 rounded-[10px] border border-[var(--field-inset-border)] bg-[var(--field-inset)] p-4"
      >
        <FieldLabel as="span" id="claim-working-hours">
          Working hours
        </FieldLabel>
        <div className="mt-2 grid gap-2.5 sm:grid-cols-2">
          <SelectField
            id="claim-from-day"
            label="From day"
            size="sm"
            value={workingHours.fromDay}
            onChange={(e) => setHours({ fromDay: e.target.value })}
            options={DAYS}
          />
          <SelectField
            id="claim-to-day"
            label="To day"
            size="sm"
            value={workingHours.toDay}
            onChange={(e) => setHours({ toDay: e.target.value })}
            options={DAYS}
          />
          <SelectField
            id="claim-open"
            label="Open"
            size="sm"
            value={workingHours.open}
            onChange={(e) => setHours({ open: e.target.value })}
            options={times}
          />
          <SelectField
            id="claim-close"
            label="Close"
            size="sm"
            value={workingHours.close}
            onChange={(e) => setHours({ close: e.target.value })}
            options={times}
          />
        </div>
      </div>
    </div>
  );
};

export default AddressStep;
